import { PropTypes } from 'prop-types';
import { useState, useEffect, useContext, useCallback } from 'react';
import AccionaTableBody from './AccionaTableBody';
import AccionaTableHeader from './AccionaTableHeader';
import AccionaTablePagination from './AccionaTablePagination';
import { MainContext } from '../../context/MainContext';
import { Empty } from 'antd';
import styles from './index.module.scss';

const { header } = styles;

const UncontrolledAccionaTable = ({
  columns,
  pageSize,
  setSelectedRows,
  selectedRows,
  showTitle,
  dataParseFunction,
  onChange,
  actions,
  initialFilters,
  expandable,
  isSelectionDisabled,
  title,
  rowHeight,
  hideSelectAllCheckbox,
  showSizeChanger,
  actionsColumnWidth,
  items = [],
  total,
  fetching,
  showPagination,
  layout = 'auto',
}) => {
  const { setLoading } = useContext(MainContext);
  const [pagination, setPagination] = useState({
    current: 1,
    total,
    pageSize,
  });
  const [filters, setFilters] = useState(
    initialFilters || {
      sortable: columns.docket_number?.ascSort ? 'docket_number-ASC' : '',
    },
  );
  const { pageSize: currentPageSize } = pagination;

  const onClickCheckBoxHeader = (checked) => {
    if (checked) {
      const allSelected = [];
      items.forEach((element) => {
        if (!isSelectionDisabled(element))
          allSelected.push(
            Object.assign(element, { page: pagination.current }),
          );
      });
      setSelectedRows(allSelected);
    } else {
      setSelectedRows([]);
    }
  };

  const onClickCheckBox = (checked, object) => {
    if (checked) {
      if (selectedRows.findIndex((x) => x.id === object.id) < 0) {
        setSelectedRows([
          ...selectedRows,
          Object.assign(object, { page: pagination.current }),
        ]);
      }
    } else {
      const index = selectedRows.findIndex((item) => item.id === object.id);
      setSelectedRows([
        ...selectedRows.slice(0, index),
        ...selectedRows.slice(index + 1),
      ]);
    }
  };

  const handleSetPagination = useCallback(
    (newPagination) => setPagination(newPagination),
    [],
  );

  const handleSetFilters = useCallback(
    (newFilters) => setFilters(newFilters),
    [],
  );

  useEffect(() => {
    if (pagination.total !== total) {
      setPagination((prev) => ({ ...prev, total }));
    }
    if (setSelectedRows) setSelectedRows([]);
  }, [total, pagination, setSelectedRows]);

  useEffect(() => {
    if (pageSize) {
      setPagination((prev) => ({ ...prev, pageSize }));
    }
  }, [pageSize]);

  useEffect(() => {
    setLoading(fetching);
  }, [fetching, setLoading]);

  useEffect(() => {
    onChange({ pagination, filters });
  }, [pagination, filters, onChange]);

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  return (
    <div className="row" id="AccionaTable">
      <div className={`col-sm-12 ${header}`}>{showTitle && title}</div>
      <div className="col-sm-12">
        <table
          className="dataTable"
          style={{ backgroundColor: '#fff', tableLayout: layout }}
        >
          <AccionaTableHeader
            isSelectionDisabled={isSelectionDisabled}
            columns={columns}
            hiddenActionCol={!actions}
            hiddenCheckBoxCol={setSelectedRows ? false : true}
            hideSelectAllCheckbox={hideSelectAllCheckbox}
            onClickCheckBoxHeader={onClickCheckBoxHeader}
            selectedRows={selectedRows}
            totalItems={items.length || 0}
            selectedPage={pagination.current}
            setFilters={handleSetFilters}
            setPagination={handleSetPagination}
            total={total}
            filters={filters}
            expandable={expandable}
            items={items}
            actionsColumnWidth={actionsColumnWidth}
            entity="none"
          />
          {items.length === 0 ? (
            <tbody>
              <tr>
                <td
                  colSpan={
                    Object.keys(columns).length + (setSelectedRows ? 2 : 1)
                  }
                >
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </td>
              </tr>
            </tbody>
          ) : (
            <AccionaTableBody
              bodyData={dataParseFunction ? dataParseFunction(items) : items}
              columns={columns}
              onClickCheckBox={setSelectedRows ? onClickCheckBox : undefined}
              selectedRows={selectedRows}
              actions={actions}
              pageSize={currentPageSize}
              expandable={expandable}
              isSelectionDisabled={isSelectionDisabled}
              rowHeight={rowHeight}
              actionsColumnWidth={actionsColumnWidth}
            />
          )}
        </table>
        {showPagination && (
          <div
            className="col-sm-12"
            style={{
              backgroundColor: '#fff',
              height: '43px',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {pagination.total !== 0 && (
              <AccionaTablePagination
                pagination={pagination}
                showSizeChanger={showSizeChanger}
                setPagination={handleSetPagination}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

UncontrolledAccionaTable.propTypes = {
  columns: PropTypes.object.isRequired,
  pageSize: PropTypes.number,
  setSelectedRows: PropTypes.func,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  showTitle: PropTypes.bool,
  dataParseFunction: PropTypes.func,
  onChange: PropTypes.func,
  actions: PropTypes.func,
  initialFilters: PropTypes.object,
  expandable: PropTypes.shape({
    rowExpandable: PropTypes.func,
    expandedRowRender: PropTypes.func,
  }),
  isSelectionDisabled: PropTypes.func,
  title: PropTypes.string,
  rowHeight: PropTypes.string,
  hideSelectAllCheckbox: PropTypes.bool,
  showSizeChanger: PropTypes.bool,
  actionsColumnWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.number.isRequired,
  fetching: PropTypes.bool,
  showPagination: PropTypes.bool,
  layout: PropTypes.oneOf(['auto', 'fixed']),
};
UncontrolledAccionaTable.defaultProps = {
  pageSize: 5,
  selectedRows: [],
  showTitle: true,
  onChange: () => {},
  columns: {},
  isSelectionDisabled: () => {},
  dataParseFunction: undefined,
  actions: undefined,
  initialFilters: undefined,
  expandable: undefined,
  title: undefined,
  rowHeight: '94px',
  setSelectedRows: undefined,
  hideSelectAllCheckbox: false,
  showSizeChanger: false,
  actionsColumnWidth: 'auto',
  showPagination: true,
  layout: 'auto',
  fetching: false,
};

export default UncontrolledAccionaTable;
